import { memo, useState } from 'react';
import type { LineItem } from '../../store/useQuoteStore';
import { useQuoteStore } from '../../store/useQuoteStore';
import { PRODUCTS } from '../../data/products';
import {
  getProductType,
  getAddonsFor,
  PRODUCT_META,
  ADDON_LABEL_COLORS,
} from '../../data/productMeta';
import { calcItemPrice, calcSqft } from '../../utils/pricing';
import { NumericInput } from '../ui';
import { OTHER_COLOUR, isCustomColour } from '../../utils/colourOptions';
import { capturesPanelCount } from '../../utils/panelCount';

interface LineItemRowProps {
  item: LineItem;
  index: number;
  cat1Options: string[];
}

/**
 * Quantity input with a local draft so the field can be cleared / edited
 * (backspace + delete work). The store keeps an integer >= 1; we only commit a
 * valid number while typing, and clamp an empty/invalid value back to 1 on
 * blur. (The old `parseInt(v) || 1` on every keystroke snapped an emptied field
 * straight back to "1", so it couldn't be cleared.)
 */
function QtyField({ qty, onCommit }: { qty: number; onCommit: (n: number) => void }) {
  // Local draft keyed off the row's item id (duplicate / reload remount this
  // component with a fresh id, so the draft re-initialises naturally — no sync
  // effect needed, and nothing else mutates a mounted row's qty).
  const [draft, setDraft] = useState(String(qty));
  return (
    <NumericInput
      className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white"
      value={draft}
      onChange={(v) => {
        setDraft(v);
        const n = parseInt(v, 10);
        if (!Number.isNaN(n) && n >= 1) onCommit(n);
      }}
      onBlur={() => {
        const n = parseInt(draft, 10);
        if (Number.isNaN(n) || n < 1) {
          setDraft('1');
          onCommit(1);
        }
      }}
    />
  );
}

function LineItemRowImpl({ item, index, cat1Options }: LineItemRowProps) {
  const updateItem = useQuoteStore((s) => s.updateItem);
  const removeItem = useQuoteStore((s) => s.removeItem);
  const addAddon = useQuoteStore((s) => s.addAddon);
  const removeAddon = useQuoteStore((s) => s.removeAddon);
  const updateAddon = useQuoteStore((s) => s.updateAddon);
  const duplicateItem = useQuoteStore((s) => s.duplicateItem);
  const moveItem = useQuoteStore((s) => s.moveItem);
  const itemCount = useQuoteStore((s) => s.items.length);
  const addItem = useQuoteStore((s) => s.addItem);
  const addAdhocItem = useQuoteStore((s) => s.addAdhocItem);

  const ROOM_OPTIONS = [
    'Living Room',
    'Dining Room',
    'Kitchen',
    'Service Yard',
    'Master Bedroom',
    'Bedroom 1',
    'Bedroom 2',
    'Bedroom 3',
    'Toilet 1',
    'Toilet 2',
    'Other',
  ];

  const handleCat1Change = (val: string) => {
    updateItem(item.id, 'cat1', val);
    updateItem(item.id, 'cat2', '');
    const mains = PRODUCTS.filter((p) => p.cat1 === val && getProductType(p.prd_code) !== 'addon');
    updateItem(item.id, 'product', mains.length === 1 ? mains[0] : null);
    setMainColourOther(false);
  };

  const handleProductChange = (prdCode: string) => {
    const p = PRODUCTS.find((p) => p.prd_code === prdCode) || null;
    updateItem(item.id, 'product', p);
    // The store clears `colour` on a product change; clear the local Others
    // flag with it, or an empty "Others" text box stays open against a product
    // whose colour list may not even have an Others option (backlog #94).
    setMainColourOther(false);
  };

  const mainAmount = calcItemPrice(item.product, item.qty, item.widthMm, item.heightMm);
  const addonAmount = (item.addons || []).reduce(
    (sum, a) =>
      sum +
      calcItemPrice(
        a.product,
        a.qty,
        a.inheritDims ? item.widthMm : a.widthMm,
        a.inheritDims ? item.heightMm : a.heightMm,
      ),
    0,
  );
  const totalAmount = mainAmount + addonAmount;

  // Manual per-line price override (mirrors calcItemTotal's guard). When valid
  // it replaces the displayed line total; the computed price is kept as a
  // struck-through reference.
  const overrideRaw = item.priceOverride ?? '';
  const overrideNum = parseFloat(overrideRaw);
  const hasOverride = overrideRaw !== '' && !Number.isNaN(overrideNum) && overrideNum >= 0;
  // A catalog override is floored at the system price (mirrors calcItemTotal);
  // an adhoc override is free-form. `totalAmount` is the catalog/adhoc line total.
  const overrideBelowSystem =
    hasOverride && item.source !== 'adhoc' && !!item.product && overrideNum < totalAmount;
  const effectiveTotal = hasOverride
    ? item.source === 'adhoc'
      ? overrideNum
      : Math.max(overrideNum, totalAmount)
    : totalAmount;

  // "Others" colour mode. A loaded custom colour (non-empty, not a preset)
  // re-opens the text box on its own via isCustomColour; the local flags let the
  // rep OPEN Others with an EMPTY box without stashing a marker in `colour`
  // (so an empty custom colour never leaks a sentinel to the cart/print).
  const [mainColourOther, setMainColourOther] = useState(false);
  const [addonColourOther, setAddonColourOther] = useState<Record<string, boolean>>({});
  const showMainOther = mainColourOther || isCustomColour(item.colour, item.product?.colours);

  const needsDims = item.product?.pricing_method === 'Area';

  const filteredProducts = item.cat1
    ? PRODUCTS.filter((p) => p.cat1 === item.cat1 && getProductType(p.prd_code) !== 'addon')
    : [];

  const addonsAvailable = item.product ? getAddonsFor(item.product.prd_code) : {};
  const hasAddons = Object.keys(addonsAvailable).length > 0;

  return (
    <div
      className={`border border-outline-variant rounded-xl p-5 hover:border-primary/30 focus-within:border-primary/60 transition-colors ${
        // Zebra tint by cart position — adjacent cards stay visually distinct in long carts.
        index % 2 === 1 ? 'bg-secondary/10' : 'bg-background'
      }`}
    >
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <span className="bg-secondary text-white text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded">
            Item {String(index + 1).padStart(2, '0')}
          </span>
          {item.product && (
            <span className="text-xs font-bold text-primary uppercase tracking-wider">
              {item.product.pricing_method}
            </span>
          )}
        </div>
        <span className="font-extrabold text-secondary text-xl">
          {hasOverride && effectiveTotal !== totalAmount && (
            <span className="text-sm font-medium text-outline line-through mr-2">
              ${totalAmount.toFixed(2)}
            </span>
          )}
          ${effectiveTotal.toFixed(2)}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <label className="flex flex-col gap-1.5 md:col-span-3">
          <span className="text-[10px] font-bold text-outline uppercase tracking-wider">
            Category
          </span>
          <select
            className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white"
            value={item.cat1}
            onChange={(e) => handleCat1Change(e.target.value)}
          >
            <option value="">Select...</option>
            {cat1Options.map((c: string) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5 md:col-span-4">
          <span className="text-[10px] font-bold text-outline uppercase tracking-wider">
            Product
          </span>
          <select
            className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white disabled:bg-surface-container-highest"
            value={item.product?.prd_code || ''}
            onChange={(e) => handleProductChange(e.target.value)}
            disabled={!item.cat1}
          >
            <option value="">Select Product...</option>
            {filteredProducts.map((p) => (
              <option key={p.prd_code} value={p.prd_code}>
                {p.description}
              </option>
            ))}
          </select>
          {item.product && (
            <div className="bg-surface-container-low p-2 rounded border border-outline-variant/50 mt-0.5 space-y-1">
              <span className="text-[11px] text-secondary font-medium leading-relaxed block">
                {item.product.description}
              </span>
              {item.product &&
                PRODUCTS.find((p) => p.prd_code === item.product!.prd_code)?.wholesale_rate !==
                  undefined && (
                  <span className="text-[10px] text-outline flex items-center gap-1.5">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-3 h-3 text-tertiary"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM6.75 9.25a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Internal Wholesale Ref: $
                    {PRODUCTS.find(
                      (p) => p.prd_code === item.product!.prd_code,
                    )!.wholesale_rate!.toFixed(2)}
                  </span>
                )}
            </div>
          )}
        </label>

        {item.product?.colours && item.product.colours.length > 0 && (
          <label className="flex flex-col gap-1.5 md:col-span-3">
            <span className="text-[10px] font-bold text-outline uppercase tracking-wider">
              Colour
            </span>
            <select
              className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white"
              value={showMainOther ? OTHER_COLOUR : (item.colour ?? '')}
              onChange={(e) => {
                if (e.target.value === OTHER_COLOUR) {
                  setMainColourOther(true);
                } else {
                  setMainColourOther(false);
                  updateItem(item.id, 'colour', e.target.value);
                }
              }}
            >
              <option value="">Select colour…</option>
              {item.product.colours.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
              <option value={OTHER_COLOUR}>Others…</option>
            </select>
            {showMainOther && (
              <input
                type="text"
                placeholder="Enter colour"
                className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white"
                value={item.colour ?? ''}
                onChange={(e) => updateItem(item.id, 'colour', e.target.value)}
              />
            )}
          </label>
        )}

        {/* Panel count — only the families where the split is settled at
            quotation (utils/panelCount.ts). Optional: blank is a valid answer
            and nothing downstream breaks on it. Descriptive only — it never
            enters pricing. */}
        {capturesPanelCount(item.product?.prd_code) && (
          <label className="flex flex-col gap-1.5 md:col-span-2">
            <span className="text-[10px] font-bold text-outline uppercase tracking-wider">
              Panels
            </span>
            {/* Stored as a string and cleared to '' — never undefined; see
                the note on LineItem.panelCount (the Admin SDK would throw). */}
            <NumericInput
              value={item.panelCount ?? ''}
              onChange={(v) => updateItem(item.id, 'panelCount', v)}
              placeholder="—"
              className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white"
            />
          </label>
        )}

        <label className="flex flex-col gap-1.5 md:col-span-2">
          <span className="text-[10px] font-bold text-outline uppercase tracking-wider">Qty</span>
          <QtyField qty={item.qty} onCommit={(n) => updateItem(item.id, 'qty', n)} />
        </label>

        <label className="flex flex-col gap-1.5 md:col-span-3">
          <span className="text-[10px] font-bold text-outline uppercase tracking-wider">Room</span>
          <select
            className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white"
            value={item.room || ''}
            onChange={(e) => updateItem(item.id, 'room', e.target.value)}
          >
            <option value="">Select Room...</option>
            {ROOM_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>

        {item.room === 'Other' && (
          <label className="flex flex-col gap-1.5 md:col-span-3">
            <span className="text-[10px] font-bold text-outline uppercase tracking-wider">
              Specify Room
            </span>
            <input
              type="text"
              placeholder="e.g. Balcony"
              className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white"
              value={item.otherRoom || ''}
              onChange={(e) => updateItem(item.id, 'otherRoom', e.target.value)}
            />
          </label>
        )}

        <label className="flex flex-col gap-1.5 md:col-span-3">
          <span className="text-[10px] font-bold text-outline uppercase tracking-wider">
            Width (mm)
          </span>
          <input
            type="number"
            placeholder="e.g. 900"
            className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white disabled:bg-surface-container-highest disabled:opacity-50"
            value={item.widthMm}
            disabled={!needsDims}
            onChange={(e) => updateItem(item.id, 'widthMm', e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1.5 md:col-span-3">
          <span className="text-[10px] font-bold text-outline uppercase tracking-wider">
            Height (mm)
          </span>
          <input
            type="number"
            placeholder="e.g. 2100"
            className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white disabled:bg-surface-container-highest disabled:opacity-50"
            value={item.heightMm}
            disabled={!needsDims}
            onChange={(e) => updateItem(item.id, 'heightMm', e.target.value)}
          />
        </label>
        <div className="flex flex-col justify-end md:col-span-6 pb-2">
          {item.product?.pricing_method === 'Area' && item.widthMm && item.heightMm ? (
            <div className="text-[11px] font-medium text-outline">
              <span className="text-secondary font-bold">
                {calcSqft(item.widthMm, item.heightMm)} sqft
              </span>{' '}
              calculated. Min: {item.product.min_area || 'None'} sqft | Rate: $
              {item.product.base_rate}/{item.product.uom}
            </div>
          ) : !needsDims && item.product ? (
            <div className="text-[11px] font-medium text-outline">
              Rate:{' '}
              <strong className="text-secondary">
                ${item.product.base_rate} {item.product.unit}
              </strong>{' '}
              (Fixed Size)
            </div>
          ) : null}
        </div>

        <label className="flex flex-col gap-1.5 md:col-span-4">
          <span className="text-[10px] font-bold text-outline uppercase tracking-wider">
            Override price{' '}
            <span className="text-outline/60 font-normal normal-case">(optional, SGD)</span>
          </span>
          <NumericInput
            decimal
            placeholder="e.g. 250.00"
            className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white"
            value={item.priceOverride ?? ''}
            onChange={(v) => updateItem(item.id, 'priceOverride', v)}
          />
        </label>

        {overrideBelowSystem && (
          <p className="md:col-span-4 -mt-1 text-[11px] font-bold text-red-600">
            Override can&apos;t be below the catalog price of ${totalAmount.toFixed(2)} — it will
            hold at ${totalAmount.toFixed(2)}. Use the Grand Total for discounts.
          </p>
        )}

        <label className="flex flex-col gap-1.5 md:col-span-12">
          <span className="text-[10px] font-bold text-outline uppercase tracking-wider">
            Remarks{' '}
            <span className="text-outline/60 font-normal normal-case">(shown to customer)</span>
          </span>
          <textarea
            rows={2}
            placeholder="e.g. Colour to match existing frames; deliver after 6pm"
            className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white resize-y"
            value={item.remarks || ''}
            onChange={(e) => updateItem(item.id, 'remarks', e.target.value)}
          />
        </label>
      </div>

      {item.product && (
        <div className="mt-5 pt-5 border-t border-dashed border-outline-variant">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold text-outline tracking-wider uppercase">
              Options & Upgrades
            </span>
            {(item.addons || []).length > 0 && (
              <span className="bg-primary/10 text-primary text-[10px] font-bold px-1.5 py-0.5 rounded">
                {(item.addons || []).length} Active
              </span>
            )}
          </div>

          {(item.addons || []).map((addon) => {
            const isPoa = addon.product.pricing_method === 'POA';
            const w = addon.inheritDims ? item.widthMm : addon.widthMm;
            const h = addon.inheritDims ? item.heightMm : addon.heightMm;
            // POA add-ons carry no rate — the rep types a flat amount (poaPrice).
            const price = isPoa
              ? parseFloat(addon.poaPrice ?? '') || 0
              : calcItemPrice(addon.product, addon.qty, w, h);
            const meta = PRODUCT_META[addon.product.prd_code] || {};
            const color = ADDON_LABEL_COLORS[meta.addonLabel || 'Option'] || '#cbd5e1';

            return (
              <div
                key={addon.id}
                className="flex flex-wrap items-center gap-3 bg-surface-container-low p-2 rounded-lg text-sm mb-2 border border-outline-variant"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: color }}
                ></span>
                <div className="flex-1 min-w-[120px]">
                  <span className="font-semibold text-secondary block">
                    {addon.product.quote_desc}
                  </span>
                  {PRODUCTS.find((p) => p.prd_code === addon.product.prd_code)?.wholesale_rate !==
                    undefined && (
                    <span className="text-[10px] text-outline flex items-center gap-1 mt-0.5">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="w-3 h-3 text-tertiary"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM6.75 9.25a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Wholesale Ref: $
                      {PRODUCTS.find(
                        (p) => p.prd_code === addon.product.prd_code,
                      )!.wholesale_rate!.toFixed(2)}
                    </span>
                  )}
                </div>

                {addon.product.colours &&
                  addon.product.colours.length > 0 &&
                  (() => {
                    const showAddonOther =
                      addonColourOther[addon.id] ||
                      isCustomColour(addon.colour, addon.product.colours);
                    return (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-outline">Colour:</span>
                        <select
                          className="border border-outline-variant rounded p-1 text-xs outline-none focus:border-primary bg-white"
                          value={showAddonOther ? OTHER_COLOUR : (addon.colour ?? '')}
                          onChange={(e) => {
                            if (e.target.value === OTHER_COLOUR) {
                              setAddonColourOther((m) => ({ ...m, [addon.id]: true }));
                            } else {
                              setAddonColourOther((m) => ({ ...m, [addon.id]: false }));
                              updateAddon(item.id, addon.id, 'colour', e.target.value);
                            }
                          }}
                        >
                          <option value="">Select…</option>
                          {addon.product.colours.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                          <option value={OTHER_COLOUR}>Others…</option>
                        </select>
                        {showAddonOther && (
                          <input
                            type="text"
                            placeholder="Enter colour"
                            className="border border-outline-variant rounded p-1 text-xs outline-none focus:border-primary bg-white w-28"
                            value={addon.colour ?? ''}
                            onChange={(e) =>
                              updateAddon(item.id, addon.id, 'colour', e.target.value)
                            }
                          />
                        )}
                      </div>
                    );
                  })()}

                {addon.inheritDims ? (
                  <span className="bg-surface-container-highest text-[10px] px-2 py-0.5 rounded font-medium text-outline">
                    Same size as main item
                  </span>
                ) : null}

                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-outline">Qty:</span>
                  <input
                    type="number"
                    min="1"
                    className="w-14 border border-outline-variant rounded p-1 text-xs outline-none focus:border-primary text-center bg-white"
                    value={addon.qty}
                    onChange={(e) =>
                      updateAddon(item.id, addon.id, 'qty', parseInt(e.target.value) || 1)
                    }
                  />
                </div>

                {isPoa ? (
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-bold text-outline">$</span>
                    <NumericInput
                      decimal
                      placeholder="Enter price"
                      className="w-24 border border-outline-variant rounded p-1 text-xs outline-none focus:border-primary text-right bg-white"
                      value={addon.poaPrice ?? ''}
                      onChange={(v) => updateAddon(item.id, addon.id, 'poaPrice', v)}
                    />
                  </div>
                ) : (
                  <span className="font-bold text-secondary w-20 text-right">
                    ${price > 0 ? price.toFixed(2) : '--'}
                  </span>
                )}
                <button
                  onClick={() => removeAddon(item.id, addon.id)}
                  className="text-outline hover:text-red-500 transition-colors p-1"
                >
                  <span className="material-symbols-outlined text-[18px]">close</span>
                </button>
              </div>
            );
          })}

          {hasAddons ? (
            <div className="flex items-center gap-2 mt-3">
              <select
                className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white flex-1 max-w-sm"
                onChange={(e) => {
                  if (e.target.value) {
                    addAddon(item.id, e.target.value);
                    e.target.value = '';
                  }
                }}
                defaultValue=""
              >
                <option value="" disabled>
                  + Add an option or upgrade...
                </option>
                {Object.entries(addonsAvailable).map(([label, prds]) => (
                  <optgroup key={label} label={label}>
                    {prds.map((p) => (
                      <option key={p.prd_code} value={p.prd_code}>
                        {p.description}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          ) : (
            <div className="text-xs text-outline italic">
              No options available for this product.
            </div>
          )}
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-outline-variant flex flex-wrap items-center gap-2">
        <button
          onClick={addItem}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-container text-primary font-bold text-sm hover:opacity-90 transition-opacity"
        >
          <span className="material-symbols-outlined text-base">add</span>
          Add Item
        </button>
        <button
          onClick={addAdhocItem}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-primary/40 text-primary font-bold text-sm hover:bg-primary/5 transition-colors"
        >
          <span className="material-symbols-outlined text-base">edit_note</span>
          Add Adhoc Item
        </button>
        <button
          type="button"
          onClick={() => moveItem(item.id, 'up')}
          disabled={index === 0}
          title="Move up"
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-outline-variant text-secondary font-bold text-sm hover:border-primary hover:text-primary transition-colors disabled:opacity-40 disabled:hover:border-outline-variant disabled:hover:text-secondary"
        >
          <span className="material-symbols-outlined text-base">arrow_upward</span>
          Move up
        </button>
        <button
          type="button"
          onClick={() => moveItem(item.id, 'down')}
          disabled={index === itemCount - 1}
          title="Move down"
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-outline-variant text-secondary font-bold text-sm hover:border-primary hover:text-primary transition-colors disabled:opacity-40 disabled:hover:border-outline-variant disabled:hover:text-secondary"
        >
          <span className="material-symbols-outlined text-base">arrow_downward</span>
          Move down
        </button>
        <button
          onClick={() => duplicateItem(item.id)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-outline-variant text-secondary font-bold text-sm hover:border-primary hover:text-primary transition-colors"
        >
          <span className="material-symbols-outlined text-base">content_copy</span>
          Duplicate
        </button>
        <button
          onClick={() => removeItem(item.id)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-outline-variant text-outline font-bold text-sm hover:border-red-400 hover:text-red-500 transition-colors ml-auto"
        >
          <span className="material-symbols-outlined text-base">delete</span>
          Delete
        </button>
      </div>
    </div>
  );
}

export default memo(LineItemRowImpl);
